<template>
	<v-card>
		<v-card-title primary-title>
			{{ title }}
			<v-spacer></v-spacer>
			<v-btn
				color="primary"
				v-if="printUrl"
				:href="printUrl"
				target="_blank"
			>
				<v-icon left>mdi-printer</v-icon>
				{{ $t("label.print") }}
			</v-btn>
			<v-btn
				v-if="exportUrl"
				:href="exportUrl"
				target="_blank"
				color="success"
				class="ml-3"
			>
				<v-icon left>mdi-file-excel</v-icon>
				{{ $t("label.export") }}
			</v-btn>
		</v-card-title>
		<v-divider></v-divider>
		<v-card-text>
			<v-data-table
				:items="data.data"
				class="elevation-0"
				:headers="headers"
				:server-items-length="data.meta.total"
				:options.sync="options"
				:page="data.current_page"
				:items-per-page="parseInt(data.per_page)"
				@update:items-per-page="onFilter()"
				@update:page="onFilter()"
				:footer-props="{
					'items-per-page-options': [25, 50, 200, 500, 1000],
				}"
				:group-by="groupBy"
			>
				<template v-slot:[`item.actions`]="{ item }">
					<v-menu bottom right>
						<template v-slot:activator="{ on }">
							<v-btn icon v-on="on">
								<v-icon>mdi-dots-vertical</v-icon>
							</v-btn>
						</template>

						<v-list nav dense>
							<template v-if="hasPrint">
								<v-list-item
									@click="$emit('onPrint', item)"
									target="_blank"
								>
									<v-list-item-icon>
										<v-icon>mdi-printer</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>{{
											$t("label.print")
										}}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-divider></v-divider>
							</template>
							<template v-if="hasView">
								<v-list-item @click="$emit('onView', item)">
									<v-list-item-icon>
										<v-icon>mdi-eye</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>{{
											$t("View")
										}}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-divider></v-divider>
							</template>
							<template v-if="hasEdit">
								<v-list-item @click="$emit('onEdit', item)">
									<v-list-item-icon>
										<v-icon>mdi-pencil</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>{{
											$t("Edit")
										}}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-divider></v-divider>
							</template>
							<template v-if="hasPassword">
								<v-list-item @click="$emit('onPassword', item)">
									<v-list-item-icon>
										<v-icon>mdi-lock</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>{{
											$t("label.reset_password")
										}}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
								<v-divider></v-divider>
							</template>
							<template v-if="hasDelete">
								<v-list-item @click="$emit('onDelete', item)">
									<v-list-item-icon>
										<v-icon>mdi-delete</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title>{{
											$t("Delete")
										}}</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</template>
						</v-list>
					</v-menu>
				</template>
				<template
					v-for="(slot, name) in $scopedSlots"
					v-slot:[name]="item"
				>
					<slot :name="name" v-bind="item"></slot>
				</template>
				<template v-slot:[`item.sort_order`]="{ item }">
					<v-btn class="handle" outlined small>
						<v-icon left>mdi-cursor-move</v-icon>
						{{ item.sort_order }}
					</v-btn>
				</template>
			</v-data-table>
		</v-card-text>
	</v-card>
</template>

<script>
import pickBy from "lodash/pickBy";
import { Link } from "@inertiajs/inertia-vue";
import Sortable from "sortablejs";
export default {
	components: {
		Link,
	},
	props: {
		endpoint: {
			type: String,
			require: true,
		},
		title: {
			type: String,
			default: "Datatable",
		},
		headers: {
			type: Array,
			default: function () {
				return [];
			},
		},
		exportUrl: {
			type: String,
			default: null,
		},
		printUrl: {
			type: String,
			required: false,
			default: null,
		},
		filter: {
			type: Object,
		},
		data: {
			type: Object,
			required: true,
			default: function () {
				return {};
			},
		},
		hasEdit: {
			type: Boolean,
			default: true,
		},
		hasDelete: {
			type: Boolean,
			default: true,
		},
		hasPrint: {
			type: Boolean,
			default: false,
		},
		hasPassword: {
			type: Boolean,
			default: false,
		},
		hasView: {
			type: Boolean,
			default: true,
		},
		sortOrder: {
			type: Boolean,
			default: false,
		},

		sortOrderEndpoint: {
			type: String,
			required: false,
		},

		groupBy: {
			type: String | Array,
			default: null,
		},
	},

	data() {
		return {
			options: {
				totalItems: 0,
			},
			loading: false,
		};
	},

	methods: {
		onFilter() {
			var filter = pickBy(this.filter);
			var params = Object.assign(
				{ filter },
				{
					page: this.options.page,
					per_page: this.options.itemsPerPage,
				}
			);

			this.$inertia.get(this.endpoint, params, {
				preserveState: true,
				preserveScroll: true,
			});
		},
	},

	mounted() {
		if (this.sortOrder) {
			let table = document.querySelector(".v-data-table tbody");
			var tenant_id = this.$page.props.tenant?.id;
			const _self = this;
			Sortable.create(table, {
				handle: ".handle",
				async onEnd({ newIndex, oldIndex }) {
					const rowSelected = _self.data.data.splice(oldIndex, 1)[0];
					_self.data.data.splice(newIndex, 0, rowSelected);

					// const { success, data } = await _self.$axios.$post(
					// 	`sort_order/${rowSelected.uuid}/outlet`,
					// 	{
					// 		newIndex: newIndex,
					// 		oldIndex: oldIndex,
					// 	}
					// );
					// if (success) {
					// 	_self.$toast(data);
					// 	_self.getItems();
					// } else {
					// 	_self.$toast.error(data);
					// }

					_self.$inertia.post(
						_self.$route(_self.sortOrderEndpoint, {
							tenant: tenant_id,
							uuid: rowSelected.uuid,
						}),
						{
							newIndex: newIndex,
							oldIndex: oldIndex,
						},
						{
							preserveScroll: true,
						}
					);
				},
			});
		}
	},
};
</script>